
import Vue from 'vue';
import { db, DocumentReference } from '@/firebase';
import {
	Avatar,
	Runnit,
	AVATAR_USE_CASE,
	IMAGE_UPLOAD_MODE,
	RUNNIT_TYPE,
	RUNNITS_ACCESS_LEVEL,
	RunnitOnboardingData,
	CompanyCustomFieldDef,
	CompanyCustomFieldKey,
} from '@run-diffusion/shared';
import _trim from 'lodash/trim';
import { mapActions, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { analytics, EVENTS } from '@/services/analytics';
import {
	DEFAULT_TEXT_MAX_LENGTH,
	getShowTextCounterAt,
	getTextMaxLengthRule,
} from '@/utils';
import GreyButton from '@/components/base/GreyButton.vue';
import HeroImageGallery from '@/views/Runnits/base/HeroImageGallery.vue';
import HeroImageLibraryGallery from '@/views/Runnits/base/HeroImageLibraryGallery.vue';
import RunnitNavTabs from '@/views/Runnits/base/RunnitNavTabs.vue';
import { ROUTER } from '@/router/constants';
import ActionsIsland from '@/components/ActionsIsland.vue';
import { IMAGE_GALLERY_DIALOG_NAV } from '@/components/ImageGallery/utils';
import ImageGalleryDialog from '@/components/ImageGallery/ImageGalleryDialog.vue';
import SelectedImagesDialog from '@/components/ImageGallery/SelectedImagesDialog.vue';
import { NODE_RUN_RESULT_SELECTION_MODE, RUNNITS_OWNER_SELECTION } from './constants';
import ImageUpload from '@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { RunnitsCRUDMixin, TeamUserMixin } from '@/mixins';
import DialogOrBottomSheet from '../../components/base/DialogOrBottomSheet.vue';
import HtmlField from '@/components/HtmlField.vue';
import ListItem from '@/components/base/ListItem.vue';
import * as amplitude from '@amplitude/analytics-browser';

export default Vue.extend({
	name: 'ConfigureRunnit',
	mixins: [
		RunnitsCRUDMixin,
		RunnitsImageSelectMixin,
		TeamUserMixin,
	],
	data () {
		const SHORT_TEXT_LENGTH: number = 100;
		return {
			RUNNITS_ACCESS_LEVEL,
			IMAGE_GALLERY_DIALOG_NAV,
			RUNNITS_OWNER_SELECTION,
			SELECTION_MODE_TRIGGER_ID: 'configure-runnit',
			IMAGE_UPLOAD_MODE,
			AVATAR_USE_CASE,
			DEFAULT_TEXT_MAX_LENGTH,
			SHORT_TEXT_LENGTH,

			// Form
			title: null,
			description: null,
			onboardingData: {} as RunnitOnboardingData,
			avatarId: null,
			avatar: null,
			customFields: {} as Record<CompanyCustomFieldKey, any>,

			formValid: false,
			rules: {
				required: [
					v => !!v || 'Required field',
				],
				title: [
					getTextMaxLengthRule(SHORT_TEXT_LENGTH),
					v => !!_trim(v) || 'Required',
				],
				description: [
					getTextMaxLengthRule(),
				],
				customField: [
					getTextMaxLengthRule(),
				],
			},

			saving: false,

			imageGalleryNavTab: null,
			isUploading: false,
			avatarPickerTabId: 'DEFAULT',
		};
	},
	watch: {
		'runnitState.runnit': {
			immediate: true,
			async handler (newVal: Runnit, oldVal: Runnit) {
				if (newVal && newVal !== oldVal) {
					this.title = this._get(newVal, 'title') || null;
					this.description = this._get(newVal, 'description') || null;
					this.onboardingData = this._get(newVal, 'onboardingData') || {};
					this.avatarId = this._get(newVal, 'avatarId') || null;
					this.avatar = this._get(newVal, 'avatar') || null;
					this.customFields = this._get(newVal, 'customFields') || {};
				}
			},
		},
		'runnitState.runnitDraft': {
			immediate: true,
			async handler (newVal: Runnit, oldVal: Runnit) {
				if (newVal !== oldVal) {
					this.title = this._get(newVal, 'title') || null;
					this.description = this._get(newVal, 'description') || null;
					this.onboardingData = this._get(newVal, 'onboardingData') || {};
					this.avatarId = this._get(newVal, 'avatarId') || null;
					this.avatar = this._get(newVal, 'avatar') || null;
					this.customFields = this._get(newVal, 'customFields') || {};
				}
			}
		},
		'runnitState.configureRunnitOpen': {
			immediate: true,
			async handler (newVal: boolean, oldVal: boolean) {
				if (newVal && !oldVal) {
					this.resetForm();
				}
			},
		},
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'team',
		]),
		runnitCustomFieldDefs (): CompanyCustomFieldDef[] {
			return this.team?.company?.runnitCustomFieldDefs || [];
		},
		avatarPickerTabs () {
			return [
				{
					id: 'DEFAULT',
					label: 'Default',
					icon: 'mdi-view-dashboard-outline',
				},
				{
					id: 'LIBRARY',
					label: 'Library',
					icon: 'mdi-image-multiple-outline',
				},
			];
		},
		createNewMode () {
			return !!this.runnitState.runnitDraft;
		},
		canEditRunnit () {
			return !!(
				this.createNewMode ||
				(
					this.runnitState?.runnit &&
					(
						this.hasManageAllGenerationsPermission ||
						(
							this.runnitState.runnit.userId === this.user.id ||
							(
								[RUNNITS_ACCESS_LEVEL.SHARED, RUNNITS_ACCESS_LEVEL.RESTRICTED].includes(this.runnitState.runnit.accessLevel) &&
								this.hasShareRunnitAssetsPermission
							)
						)
					)
				)
			);
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
		]),
		getShowTextCounterAt,
		updateCustomField (fieldKey: CompanyCustomFieldKey, value: any) {
			this.customFields = {
				...this.customFields,
				[fieldKey]: value,
			};
		},
		setDialog (isOpen) {
			const configureRunnitOpen: boolean = isOpen || false;
			if (this.createNewMode) {
				this.updateRunnitState({
					configureRunnitOpen,
					runnitDraft: null,
					runnitDraftNodeRef: null,
				});
			} else {
				this.updateRunnitState({
					configureRunnitOpen,
				});
			}
		},
		resetForm () {
			if (this.createNewMode) {
				this.title = this._get(this.runnitState.runnitDraft, `title`) || null;
				this.description = this._get(this.runnitState.runnitDraft, `description`) || null;
				this.onboardingData = this._get(this.runnitState.runnitDraft, 'onboardingData') || {};
				this.avatarId = this._get(this.runnitState.runnitDraft, 'avatarId') || null;
				this.avatar = this._get(this.runnitState.runnitDraft, 'avatar') || null;
				this.customFields = this._get(this.runnitState.runnitDraft, 'customFields') || {};
				return;
			}
			this.title = this._get(this.runnitState.runnit, `title`) || null;
			this.description = this._get(this.runnitState.runnit, `description`) || null;
			this.onboardingData = this._get(this.runnitState.runnit, 'onboardingData') || {};
			this.avatarId = this._get(this.runnitState.runnit, 'avatarId') || null;
			this.avatar = this._get(this.runnitState.runnit, 'avatar') || null;
			this.customFields = this._get(this.runnitState.runnit, 'customFields') || {};
		},
		goToSubscriptionsRoute () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_SUBSCRIPTIONS });
			this.setDialog(false);
		},
		async onCreateNewRunnit () {
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			let createdRunnit: Runnit | null = null;
			try {
				this.saving = true;

				// Process custom fields only for team mode
				const customFields: Record<CompanyCustomFieldKey, any> = {};
				if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
					this.runnitCustomFieldDefs.forEach((fieldDef: CompanyCustomFieldDef) => {
						switch (fieldDef.type) {
							case 'STR': {
								const val: string = _trim(this.customFields[fieldDef.key]);
								if (val) customFields[fieldDef.key] = val;
								break;
							}
							default:
								break;
						}
					});
				}

				const newRunnit: Runnit = {
					...this.runnitState.runnitDraft,
					title: this.title,
					description: this.description,
					onboardingData: this.onboardingData,
					avatarId: this.avatarId,
					avatar: this.avatar,
					accessLevel: RUNNITS_ACCESS_LEVEL.PRIVATE, // Always set to PRIVATE for new runnits
					...(this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && { customFields }),
				};
				// If this is a template clone, use the createRunnitFromTemplate method
				if (this.runnitState.cloneFromTemplate && this.runnitState.templateRunnit) {
					createdRunnit = await this.createRunnitFromTemplate(this.runnitState.templateRunnit, newRunnit, false);
					if (
						createdRunnit?.id &&
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.hasShareRunnitAssetsPermission
					) {
						this.updateRunnitState({
							cloneFromTemplate: false,
							templateRunnit: null,
							runnitManageAccessOpen: true,
							runnit: createdRunnit,
						});
					} else {
						// Clear the template cloning flags
						this.updateRunnitState({
							cloneFromTemplate: false,
							templateRunnit: null,
						});
					}
				} else {
					// Regular runnit creation
					createdRunnit = await this.createNewRunnit(newRunnit, this.runnitState.runnitDraftNodeRef, RUNNIT_TYPE.WORKFLOW, {}, false);
					if (
						createdRunnit?.id &&
						this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
						this.hasShareRunnitAssetsPermission
					) {
						this.updateRunnitState({
							runnitManageAccessOpen: true,
							runnit: createdRunnit,
						});
					}
				}

				if (createdRunnit?.id) {
					analytics.track(EVENTS.BOARD_CREATED, {
						runnit_id: createdRunnit.id,
						source: this.runnitState.cloneFromTemplate ? 'template_clone' : 'new',
						is_team: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM,
					});
				}

				// Keep the historical Amplitude event name even though UI
				// copy now reads "Board" — renaming the event would split
				// the data series and break existing dashboards.
				amplitude.track(`Runnit Board Created`, {
					fromTemplate: !!this.runnitState.cloneFromTemplate,
					templateId: this.runnitState.templateRunnit?.id || null,
					templateTitle: this.runnitState.templateRunnit?.title || null,
				});
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error! Problem creating the Board, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			} finally {
				this.saving = false;
				this.setDialog(false);
				if (createdRunnit?.id) {
					this.setPendingRunnit({ id: createdRunnit.id, type: createdRunnit.type });
					this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_BOARD, params: { runnitId: createdRunnit.id } });
				}
			}
		},
		async onSaveRunnitSettings () {
			if (!this.canEditRunnit) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error: Insufficient permission to save Board`,
					show: true,
				});
				this.setDialog(false);
				return
			}
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			try {
				this.saving = true;

				// Process custom fields only for team mode
				const customFields: Record<CompanyCustomFieldKey, any> = {};
				if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
					this.runnitCustomFieldDefs.forEach((fieldDef: CompanyCustomFieldDef) => {
						switch (fieldDef.type) {
							case 'STR': {
								const val: string = _trim(this.customFields[fieldDef.key]);
								if (val) customFields[fieldDef.key] = val;
								break;
							}
							default:
								break;
						}
					});
				}

				// Update runnit without changing access level
				const runnitRef: DocumentReference = db.doc(`runnits/${this.runnitState.runnit.id}`);
				const updateData: any = {
					title: _trim(this.title) || null,
					description: _trim(this.description) || null,
					onboardingData: this.onboardingData,
					...(this.avatarId ? {
						avatarId: this.avatarId,
						avatar: db.doc(`avatars/${this.avatarId}`)
					} : {
						avatarId: null,
						avatar: null,
					}),
				};

				// Only include customFields for team mode
				if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
					updateData.customFields = customFields;
				}

				await runnitRef.update(updateData);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! Board Settings saved`,
					show: true,
				});
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error! Problem saving the Board Settings, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			} finally {
				this.saving = false;
				this.setDialog(false);
			}
		},
		handleImageUpload (avatar: Avatar) {
			this.avatarId = this._get(avatar, 'id');
			this.avatar = avatar;
		},
		openImageGallery (tab) {
			this.imageGalleryNavTab = tab;
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
			});
			this.enterSelectionMode(NODE_RUN_RESULT_SELECTION_MODE.SINGLE, this.SELECTION_MODE_TRIGGER_ID, null, ['IMG']);
		},
		clearSelectedImage () {
			this.updateRunnitState({
				fieldAwaitingImageGallerySelection: null,
			});
			this.exitSelectionMode(this.SELECTION_MODE_TRIGGER_ID);
		},
		handleImageSelected (avatar: Avatar) { // note: this is an avatarRef
			this.avatarId = avatar.id;
			this.avatar = avatar;
		},
		handleLibraryAvatarCreated (avatar: Avatar) {
			this.avatarId = avatar.id;
			this.avatar = avatar;
		},
	},
	components: {
		ActionsIsland,
		HeroImageGallery,
		HeroImageLibraryGallery,
		RunnitNavTabs,
		GreyButton,
		ImageGalleryDialog,
		SelectedImagesDialog,
		ImageUpload,
		RunnitImage,
		DialogOrBottomSheet,
		HtmlField,
		ListItem,
	},
});
